import React, { useContext, useState } from 'react'
import './style/sidebar-bottom-menu.scss'

import { Menu, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { CodeOutlined, LogoutOutlined, QuestionCircleFilled, StarFilled, UserOutlined } from '@ant-design/icons'
import { SidebarThemeToggle } from './sidebar-theme-toggle'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { AuthService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { observer } from 'mobx-react'
import { NoTranslate } from '../no-translate/no-translate'
import { useService } from '../../hooks/use-service'
import { PlatformUserContext, UserReviewStatus } from '../../providers/user-provider/user-provider'
import G2ColorIcon from '../icons/g2-icon-color'
import { AntdClassnameTokens } from '../../styles/antd-classname-tokens'

declare const PlatformNotifications: any

type MenuItem = NonNullable<MenuProps['items']>[number]

const menuClassNames = AntdClassnameTokens.Menu({ prefix: 'sidebar' })

const SidebarBottomMenu: React.FC = observer(() => {
    const appState = Container.get(AppState)
    const appService = Container.get(AppService)
    const authService = useService<AuthService>(AuthService)
    const userCtx = useContext(PlatformUserContext)
    const appSvc = useService(AppService)

    const { currentUser } = appState
    const profilePath = `/users/${currentUser?.id}`

    // G2 Review state
    const [reviewModalOpen, setReviewModalOpen] = useState(false)
    const domainHasReviewFlag = appState.currentDomain?.flags?.includes('FEAT_G2_IN_APP_REVIEW')
    const canShowG2 =
        userCtx.authenticated &&
        domainHasReviewFlag &&
        !('isInternalUser' in userCtx ? userCtx.user?.isInternalUser : true) &&
        ('reviewState' in userCtx ? userCtx.reviewState?.status !== UserReviewStatus.COMPLETED : false)

    const handleG2Click = () => {
        if (!userCtx.authenticated) return
        const nextStatus = UserReviewStatus.ACKNOWLEDGED
        if (userCtx.reviewState.status !== UserReviewStatus.DISMISSED) {
            appSvc.setG2ReviewStatus(userCtx.user.id, { status: nextStatus })
            userCtx.assignUser!((prev) => ({
                ...prev,
                reviewState: {
                    ...('reviewState' in prev ? prev.reviewState : {}),
                    status: nextStatus,
                },
            }))
        }
        setReviewModalOpen(true)
    }

    const userChildren: MenuItem[] = [
        ...(currentUser?.isInternalUser
            ? [
                  {
                      key: 'theme-toggle',
                      label: <SidebarThemeToggle />,
                      className: 'sidebar-theme-toggle-item',
                  },
                  { type: 'divider' as const, key: 'theme-divider' },
              ]
            : []),
        {
            key: 'user-settings',
            icon: <CodeOutlined aria-hidden="true" />,
            label: 'User Settings',
        },
        ...(PlatformNotifications.supported
            ? [
                  {
                      key: 'register-preview-device',
                      icon: <StarFilled aria-hidden="true" />,
                      label: 'Register Preview Device',
                  },
              ]
            : []),
        {
            key: 'logout',
            icon: <LogoutOutlined aria-hidden="true" />,
            label: 'Logout',
            danger: true,
        },
    ]

    const items: MenuItem[] = [
        ...(canShowG2
            ? [
                  {
                      key: 'g2-review',
                      icon: <G2ColorIcon className="g2-icon" aria-hidden="true" />,
                      label: 'Review us on G2!',
                  } as MenuItem,
              ]
            : []),
        {
            key: 'help',
            icon: <QuestionCircleFilled aria-hidden="true" />,
            label: (
                <a
                    href={appState.documentationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    Help
                </a>
            ),
        },
        {
            key: 'user',
            icon: <UserOutlined aria-hidden="true" />,
            label: <NoTranslate>{currentUser?.name}</NoTranslate>,
            children: userChildren,
            className: menuClassNames.subMenu.trigger,
            popupClassName: appState.inOrgContext ? 'sidebar-submenu-org-context' : undefined,
        },
    ]

    return (
        <>
            <Menu
                className="sidebar-bottom-menu"
                theme="dark"
                mode="vertical"
                selectable={false}
                triggerSubMenuAction="click"
                onClick={({ key, domEvent }) => {
                    switch (key) {
                        case 'g2-review':
                            handleG2Click()
                            break
                        case 'user-settings':
                            if (domEvent.metaKey) {
                                window.open(profilePath)
                            } else {
                                appService.route(profilePath)
                            }
                            break
                        case 'register-preview-device':
                            window.open('/register-preview-device', '_blank', 'noopener,noreferrer')
                            break
                        case 'logout':
                            authService.logout(false).catch(() => {})
                            break
                    }
                }}
                classNames={menuClassNames}
                items={items}
            />

            {canShowG2 && (
                <Modal
                    className="g2-review-modal"
                    open={reviewModalOpen}
                    onCancel={() => setReviewModalOpen(false)}
                    closable
                    footer={null}
                >
                    <iframe src={userCtx.reviewState.url} />
                </Modal>
            )}
        </>
    )
})

export default SidebarBottomMenu
